<template>
    <div class="survey-say">
        <div class="survey-say--avatar">
            <img
                v-if="avatarSrc && (isActive || isFollowedByInput)"
                :src="avatarSrc"
                alt="Avatar"
            />
        </div>
        <div class="survey-say--bubble" :class="{ active: isActive }">
            <span v-show="isLoading" class="dot dot-1"></span>
            <span v-show="isLoading" class="dot dot-2"></span>
            <span v-show="isLoading" class="dot dot-3"></span>
            <span
                v-show="!isLoading && !hasReselectScore"
                v-html="formattedMessage"
            ></span>
            <span v-show="!isLoading && hasReselectScore">
                <span v-html="formattedMessageBeforeReselect"></span>
                <ConversationItemReselectScore
                    :score="props.surveyParams.answer || 0"
                    :question-type="surveyParams.questionType"
                    :interact="isInteractionEnabled"
                    :csat-labels="props.csatLabels"
                    @updatescore="handleUpdateScore"
                />
                <span v-html="formattedMessageAfterReselect"></span>
            </span>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import {
    Options,
    SurveyDisplayValues,
    SurveyParams,
} from '@/entities/conversation'
import ConversationItemReselectScore from './ConversationItemReselectScore.vue'
import snarkdown from 'snarkdown'

interface Props {
    message: string
    surveyDisplayValues?: SurveyDisplayValues | unknown
    surveyParams: SurveyParams
    avatarSrc?: string
    isActive: boolean
    isFollowedByInput?: boolean
    csatLabels: Options
}

const props = defineProps<Props>()
const emit = defineEmits(['callNext', 'updateScore'])

const isLoading = ref(true)
const hasReselectScore = ref(false)
const messageBeforeReselect = ref('')
const messageAfterReselect = ref('')

const backgroundColor = computed(() =>
    props.isActive || props.isFollowedByInput ? 'var(--brandColor)' : '#fff'
)
const textColor = computed(() =>
    props.isActive || props.isFollowedByInput ? '#fff' : 'var(--brandColor)'
)

const formattedMessage = computed(() =>
    snarkdown(props.message.replace(/\n/gi, '<br/>').trim())
)

const formattedMessageBeforeReselect = computed(() =>
    snarkdown(messageBeforeReselect.value.replace(/\n/gi, '<br/>').trim())
)

const formattedMessageAfterReselect = computed(() =>
    snarkdown(messageAfterReselect.value.replace(/\n/gi, '<br/>').trim())
)

const hasPreviousReselectScore = computed(
    () => props.surveyDisplayValues?.hasReselectScore > 1
)

const isInteractionEnabled = computed(() => {
    if (hasPreviousReselectScore.value === true) {
        return false
    }

    if (props.isActive || props.isFollowedByInput) {
        return true
    }

    return false
})

onMounted(() => {
    if (props.isActive) {
        setTimeout(() => {
            isLoading.value = false
            emit('callNext', { hasReselectScore: hasReselectScore.value })
        }, 1000)
    } else {
        isLoading.value = false
    }

    if (props.message.includes('{answer}')) {
        hasReselectScore.value = true
        const splittedMessage = props.message.split('{answer}')
        messageBeforeReselect.value = splittedMessage.shift()
        messageAfterReselect.value = splittedMessage.join(
            props.surveyParams.answer
        )
    }
})

const handleUpdateScore = (data) => {
    emit('updateScore', data)
}
</script>

<style lang="less">
@import '~@/styles/rain/colour.less';

.survey-say {
    display: grid;
    grid-template-columns: 40px 1fr;
    column-gap: 20px;

    &--avatar img {
        width: 40px;
        height: 40px;
        background: #ebebeb;
        border: 3px solid white;
        border-radius: 25px;
    }

    &--bubble {
        display: inline-block;
        font-family: Roboto, Arial, Helvetica, sans-serif;
        background-color: v-bind(backgroundColor);
        border-radius: 0 20px 20px 20px;
        border: 1px solid v-bind(textColor);
        padding: 12px 20px;
        margin-right: 50px;
        color: v-bind(textColor);
        width: fit-content;

        .dot {
            width: 10px;
            height: 10px;
            background: #fff;
            display: inline-block;
            border-radius: 10px;
            margin: 7px 5px 0px 5px;
        }
        .dot-1 {
            animation: jump 1s ease-in-out 0s infinite;
        }
        .dot-2 {
            animation: jump 1s ease-in-out 100ms infinite;
        }
        .dot-3 {
            animation: jump 1s ease-in-out 200ms infinite;
        }

        a {
            color: v-bind(textColor);
        }
    }
}

@keyframes jump {
    0% {
        transform: translateY(0px);
    }
    25% {
        transform: translateY(-10px);
    }
    50% {
        transform: translateY(5px);
    }
    70% {
        transform: translateY(0px);
    }
    100% {
        transform: translateY(0px);
    }
}
</style>
